import { useMemo, useState } from 'react';

import { Typography } from '@mui/material';
import clsx from 'clsx';

import {
  IcAudioFile,
  IcClose,
  IcFileX,
  IcImage,
  IcNote,
  IcVideoRecorder,
} from '@/assets/images';
import { IFile } from '@/models';
import { fileSizeConverter, getFileType } from '@/utils';

import { FilePreviewModal, FormInputField } from '..';
import { FileListItemRoot } from './styles';

interface FileListItemProps {
  sasToken?: string;
  className?: string;
  item: IFile & { id?: string };
  enableEditDescription?: boolean;
  onRemove: () => void;
  index: number;
  disabled?: boolean;
  readOnly?: boolean;
}

export const FileListItem = (props: FileListItemProps) => {
  const {
    className,
    onRemove,
    item,
    sasToken,
    enableEditDescription,
    index,
    disabled = false,
    readOnly = false,
  } = props;
  const { name, size, error, mimeType } = item;

  const [fileViewModal, setFileViewModal] = useState<{
    open: boolean;
    file?: IFile;
  }>({
    open: false,
  });

  const isPreviewUnavailable = useMemo(() => {
    return !item || !sasToken;
  }, [item, sasToken]);

  const renderFileIcon = () => {
    if (error) {
      return <IcFileX />;
    }
    const fileType = getFileType(mimeType);

    if (fileType === 'image') return <IcImage />;
    if (fileType === 'video') return <IcVideoRecorder />;
    if (fileType === 'audio') return <IcAudioFile />;
    return <IcNote />;
  };
  const handleModalOpen = () => {
    if (isPreviewUnavailable) {
      return;
    }
    setFileViewModal({ open: true, file: item });
  };

  return (
    <FileListItemRoot className={className}>
      <div
        className="icon_box"
        onClick={handleModalOpen}
        style={{ cursor: isPreviewUnavailable ? 'not-allowed' : 'pointer' }}
      >
        {renderFileIcon()}
      </div>
      <div
        className={clsx('content_box', {
          'edit-description': enableEditDescription,
        })}
      >
        <div>
          <Typography className="txt_name">{name}</Typography>
          {!error ? (
            <Typography className="txt_size">
              {fileSizeConverter(size)}
            </Typography>
          ) : (
            <Typography className="txt_error">{error}</Typography>
          )}
        </div>
        {enableEditDescription && (
          <FormInputField
            key={item.id || item._id || `file-${index}`}
            defaultValue={item.description || ''}
            name={`files.${index}.description`}
            style={{ marginTop: '8px' }}
            placeholder="Description"
            rules={{
              maxLength: {
                value: 100,
                message: 'Description must be 100 characters or less.',
              },
            }}
          />
        )}
        {!disabled && (
          <IcClose
            className="icon_remove"
            onClick={onRemove}
            style={{ marginLeft: enableEditDescription ? '15px' : '' }}
          />
        )}
      </div>
      <FilePreviewModal
        open={fileViewModal.open}
        file={fileViewModal.file}
        readOnly={readOnly}
        onClose={() => {
          setFileViewModal({ open: false });
        }}
      />
    </FileListItemRoot>
  );
};
